// ============================================
// CREATE RECOMMEND HOOKS
// Combines hooks from:
// - [RecommendWhoOptions.tsx] -> CreateRecommend (WHO 001-019)
// - [RecommendWhatOptions.tsx] -> CreateRecommend (WHAT 020-039)
// - [RecommendHowOptions.tsx] -> CreateRecommend (HOW 040-059)
// - [RecommendSPPOptions.tsx] -> CreateRecommend (HOW 040-059)
// - [RecommendWhenOptions.tsx] -> CreateRecommend (WHEN 060-079)
// - [RecommendPlanOptions.tsx] -> CreateRecommend (PLAN 080-099)
// ============================================

import React, {useEffect, useMemo} from 'react';
import {useAccountContext} from '../../../contexts/AccountContext';
import OWNERSHIP from '../../../constants/Ownership';
import {OVRecommendation} from '../../../schema-types/OVRecommendation';
import {recommendFieldsType} from '../CreateRecommend/recommendFields';
import {ReviewDetailsFragment} from '../../../schema-types/ReviewDetailsFragment';
import {useRecommendContext} from '../CreateRecommend/CreateRecommendHelpers';
import {Option} from '../../../components/AutoForm/AutoFieldNew';
import {
  useAutoForm,
  useAutoField,
  AutoFieldHelpers,
  SetFieldValueWithSourceKey,
} from '../../../components/AutoForm/useAutoForm';
import {
  AccountBasicFragment,
  AccountBasicFragment_contacts,
} from '../../../schema-types/AccountBasicFragment';
import {UPDATES} from '../../../shared/UPDATES';
import {
  RECOMMENDATION_TYPES,
  SPP_RECOMMENDATION_TYPES,
} from '../../../constants/SolutionType';
import {URGENCY} from '../../../constants/Urgency';

// ============================================
// WHO (001-019) - from RecommendWhoOptions.tsx
// ============================================

export const loadWhoRecommends = ({
  recommendDetails,
  reviewDetails,
  setFieldValue,
  account,
}: {
  recommendDetails: OVRecommendation | null;
  reviewDetails?: ReviewDetailsFragment;
  account: AccountBasicFragment | undefined;
  setFieldValue: SetFieldValueWithSourceKey<recommendFieldsType>;
}) => {
  // if (!reviewDetails) {
  //   return;
  // }
  // if (recommendDetails?.contact?.id) {
  //   setFieldValue(UPDATES._001, 'contact', recommendDetails.contact.id);
  // } else if (reviewDetails?.contact?.id) {
  //   setFieldValue(UPDATES._002, 'contact', reviewDetails.contact.id);
  // } else if (account?.isTrust) {
  //   setFieldValue(UPDATES._003, 'contact', OWNERSHIP.TRUST);
  // } else {
  //   setFieldValue(UPDATES._004, 'contact', OWNERSHIP.JOINT_CODE);
  // }
  // // setFieldValue(UPDATES._005, 'ownership', recommendDetails?.ownership);
  // setFieldValue(UPDATES._006, 'goal', recommendDetails?.goal);
};

export const setReccContact = ({
  contacts,
  setFieldValue,
  setLabel,
  reviewDetails,
  recommendDetails,
  values,
  account,
}: {
  setLabel: React.Dispatch<React.SetStateAction<string>>;
  setFieldValue: SetFieldValueWithSourceKey<recommendFieldsType>;
  contacts: AccountBasicFragment_contacts[];
  reviewDetails: ReviewDetailsFragment | undefined;
  recommendDetails: OVRecommendation | null;
  values: recommendFieldsType;
  account: AccountBasicFragment | undefined;
}) => {
  if (!reviewDetails) {
    return;
  }

  if (account?.isTrust) {
    // setFieldValue('ownership', OWNERSHIP.WHOLE_FAMILY, UPDATES._007);
    setLabel('for an account');

    setFieldValue(UPDATES._008, 'contact', OWNERSHIP.TRUST);
  } else {
    if (!values.contact) {
      debugger;
      const defaultContact = contacts && contacts.length ? contacts[0].id : null;

      setFieldValue(
        UPDATES._009,
        'contact',
        recommendDetails?.contact?.id ||
          reviewDetails?.contact?.id ||
          (contacts.length > 1 ? OWNERSHIP.JOINT_CODE : defaultContact)
      );
    }
    if (values.contact === OWNERSHIP.JOINT_CODE) {
      // setFieldValue('ownership', OWNERSHIP.JOINT_LIVES, UPDATES._010);
      setLabel('for a joint account');
    } else if (values.contact) {
      const contact = contacts.find((c) => c.id === values.contact);

      // If the contact is not found that means we have a value
      // that's no longer present or allowed
      if (contact) {
        setLabel(`for ${contact.simplifiedName}`);
        // setFieldValue('ownership', OWNERSHIP.SINGLE_LIVES, UPDATES._011);
      } else {
        setLabel('for a contact');
      }
    }
  }
  if (recommendDetails?.goal) {
    setFieldValue(UPDATES._006, 'goal', recommendDetails?.goal);
  }
};

// ============================================
// WHAT (020-039) - from RecommendWhatOptions.tsx
// ============================================

export const loadPlanActionFromRecommend = ({
  values,
  recommendDetails,
  setFieldValue,
}: {
  values: recommendFieldsType;
  recommendDetails: OVRecommendation | null;
  setFieldValue: SetFieldValueWithSourceKey<recommendFieldsType>;
}) => {
  if (values.planAction) {
    return;
  }
  if (recommendDetails) {
    const newPlanAction = recommendDetails.action ?? undefined;
    setFieldValue(UPDATES._021, 'planAction', newPlanAction);
  }
};

export const useLoadPlanActionFromRecommend = () => {
  const {values, setFieldValue} = useAutoForm<recommendFieldsType>();
  const {recommendDetails} = useRecommendContext();
  useEffect(() => {
    loadPlanActionFromRecommend({values, recommendDetails, setFieldValue});
  }, [recommendDetails]);
};

// ============================================
// HOW (040-059) - from RecommendHowOptions.tsx
// ============================================

export const loadRecommendsHowOrFromReview = ({
  recommendDetails,
  setFieldValue,
  reviewDetails,
}: {
  setFieldValue: SetFieldValueWithSourceKey<recommendFieldsType>;
  reviewDetails: ReviewDetailsFragment | undefined;
  recommendDetails: OVRecommendation | null;
}) => {
  // if there's data to be loaded
  if (recommendDetails) {
    setFieldValue(
      UPDATES._040,
      'how',
      recommendDetails?.isXo
        ? RECOMMENDATION_TYPES.Instruction
        : RECOMMENDATION_TYPES.Recommendation
    );
    return;
  }
  if (!reviewDetails) {
    return;
  }
  // derive from the review details
  if (reviewDetails?.executionOnly) {
    setFieldValue(UPDATES._042, 'how', RECOMMENDATION_TYPES.Instruction);
  } else if (reviewDetails) {
    setFieldValue(UPDATES._043, 'how', RECOMMENDATION_TYPES.Recommendation);
  }
};

// ============================================
// HOW (040-059) - from RecommendSPPOptions.tsx
// ============================================

export const loadSppRecommends = ({
  recommendDetails,
  setFieldValue,
}: {
  setFieldValue: SetFieldValueWithSourceKey<recommendFieldsType>;
  recommendDetails: OVRecommendation | null;
}) => {
  // if there's data to be loaded
  if (recommendDetails) {
    setFieldValue(
      UPDATES._040,
      'sppType',
      recommendDetails?.isSppDaXo
        ? SPP_RECOMMENDATION_TYPES.ExecutionOnly
        : SPP_RECOMMENDATION_TYPES.Advised
    );
  }
  setFieldValue(UPDATES._041, 'how', RECOMMENDATION_TYPES.Instruction);
};

// ============================================
// WHEN (060-079) - from RecommendWhenOptions.tsx
// ============================================

export const loadUrgencyFromRecommend = ({
  recommendDetails,
  values,
  setFieldValue,
}: {
  recommendDetails: OVRecommendation | null;
  values: recommendFieldsType;
  setFieldValue: SetFieldValueWithSourceKey<recommendFieldsType>;
}) => {
  setFieldValue(UPDATES._060, 'urgency', recommendDetails?.urgency || URGENCY.NORMAL_ID);
  if (!values.urgencyReason) {
    setFieldValue(UPDATES._061, 'urgencyReason', recommendDetails?.urgencyReason);
  }
};

export const useLoadUrgencyFromRecommend = () => {
  const {values, setFieldValue} = useAutoForm<recommendFieldsType>();
  const {recommendDetails} = useRecommendContext();
  useEffect(() => {
    loadUrgencyFromRecommend({recommendDetails, values, setFieldValue});
  }, []);
};

export const loadUrgencyDefaults = ({
  urgencyValue,
  urgencyHelpers,
  urgencyReasonHelpers,
}: {
  urgencyValue: recommendFieldsType['urgency'];
  urgencyHelpers: AutoFieldHelpers<recommendFieldsType['urgency']>;
  urgencyReasonHelpers: AutoFieldHelpers<recommendFieldsType['urgencyReason']>;
}) => {
  if (!urgencyValue) {
    urgencyHelpers.setValue(UPDATES._062, URGENCY.NORMAL_ID);
  }
  if (urgencyValue === URGENCY.TAX_ID) {
    urgencyReasonHelpers.setValue(UPDATES._063, 'Tax year end');
  } else {
    urgencyReasonHelpers.setValue(UPDATES._064, '');
  }
};

export const useLoadUrgencyDefaults = () => {
  const [urgencyInput, , urgencyHelpers] =
    useAutoField<recommendFieldsType['urgency']>('urgency');
  const [, , urgencyReasonHelpers] =
    useAutoField<recommendFieldsType['urgencyReason']>('urgencyReason');
  const {value: urgencyValue} = urgencyInput;
  useEffect(() => {
    loadUrgencyDefaults({urgencyValue, urgencyHelpers, urgencyReasonHelpers});
  }, [urgencyValue]);
};

// ============================================
// PLAN (080-099) - from RecommendPlanOptions.tsx
// ============================================

export const loadMainPlanType = ({
  recommendDetails,
  setFieldValue,
}: {
  recommendDetails: OVRecommendation | null;
  setFieldValue: SetFieldValueWithSourceKey<recommendFieldsType>;
}) => {
  if (!recommendDetails?.planType) {
    return;
  }
  setFieldValue(UPDATES._080, 'mainPlanType', recommendDetails?.planType);
};

export const useLoadMainPlanType = () => {
  const {setFieldValue} = useAutoForm<recommendFieldsType>();
  const {recommendDetails} = useRecommendContext();
  useEffect(() => {
    loadMainPlanType({recommendDetails, setFieldValue});
  }, [recommendDetails?.planType]);
};

// ============================================
// COMPUTED FIELDS (not stored in state)
// ============================================

export const useIsMinor = (): boolean => {
  const {values, setFieldValue} = useAutoForm<recommendFieldsType>();
  const {account} = useAccountContext();
  const contacts = account?.contacts || [];
  const isMinor = useMemo(() => {
    if (!values.contact) {
      return false;
    }
    const contact = contacts.find((c) => c.id === values.contact);
    return Boolean(contact?.isMinor);
  }, [contacts, values.contact]);

  useEffect(() => {
    setFieldValue(UPDATES._013, 'isMinor', isMinor);
  }, [isMinor]);

  return isMinor;
};

export const useAdviceWhat = (
  planActionsDynamic: Record<
    string,
    {optionLabel: string; label: string; adviceLabel: string}
  >
): string => {
  const {values} = useAutoForm<recommendFieldsType>();
  return useMemo(() => {
    if (!values.planAction) {
      return '';
    }
    return planActionsDynamic[values.planAction]?.adviceLabel || '';
  }, [values.planAction, planActionsDynamic]);
};

export const useAdvicePlan = (planTypes: Option<string>[]): string => {
  const {values} = useAutoForm<recommendFieldsType>();
  return useMemo(() => {
    if (!values.mainPlanType) {
      return '';
    }
    const plan = planTypes.find((p) => p.value === values.mainPlanType);
    if (!plan) {
      return '';
    }
    return plan.capitalise ? plan.label : plan.label.toLowerCase();
  }, [values.mainPlanType, planTypes]);
};
